import { formatSignedPercent } from './format';

// Heatmap of shares (docs/heatmap/SPEC.md): squarified treemap grouped by sector, small shares left off the
// map, colour steps, label levels.
// Pure functions: the screen keeps the layout while open and only recolours tiles (decision 9).

// Day change from the base close (decision 17); null without a price or a close.
export function dayChange(price, prevClose) {
  const p = Number(price);
  const c = Number(prevClose);
  if (!price || !prevClose || !Number.isFinite(p) || !Number.isFinite(c) || c <= 0) {
    return null;
  }
  return (p / c - 1) * 100;
}

// Shares below this part of the sum stay off the map, listed under «Смотреть все» (decisions 21, 26):
// on real turnovers about 7 of the 44 index shares.
export const OTHERS_SHARE = { phone: 0.003, desktop: 0.001 };

/**
 * Splits items { key, weight } into tiles and the rest. A single small one stays a tile: leaving out
 * one share would not make room for anything.
 */
export function groupSmall(items, minShare) {
  const positive = items.filter((i) => i.weight > 0).sort((a, b) => b.weight - a.weight);
  const total = positive.reduce((s, i) => s + i.weight, 0);
  if (total <= 0) {
    return { tiles: [], others: [] };
  }
  const small = positive.filter((i) => i.weight / total < minShare);
  if (small.length < 2) {
    return { tiles: positive, others: [] };
  }
  const tiles = positive.filter((i) => i.weight / total >= minShare);
  return { tiles, others: small };
}

/**
 * Squarified treemap (Bruls, Huizing, van Wijk): items { key, weight } sorted by weight, biggest first,
 * laid out in the rectangle. Returns [{ key, x, y, w, h }] in the same order.
 */
export function squarify(items, width, height) {
  const total = items.reduce((s, i) => s + i.weight, 0);
  if (total <= 0 || width <= 0 || height <= 0) {
    return [];
  }
  const scale = (width * height) / total;
  const areas = items.map((i) => ({ key: i.key, area: i.weight * scale }));
  const rects = [];
  let x = 0;
  let y = 0;
  let w = width;
  let h = height;
  let row = [];

  const worst = (list, side) => {
    const sum = list.reduce((s, i) => s + i.area, 0);
    const max = Math.max(...list.map((i) => i.area));
    const min = Math.min(...list.map((i) => i.area));
    return Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min));
  };

  const layoutRow = (list) => {
    const sum = list.reduce((s, i) => s + i.area, 0);
    if (w >= h) {
      // Column along the left edge
      const colW = sum / h;
      let cy = y;
      for (const item of list) {
        const ih = item.area / colW;
        rects.push({ key: item.key, x, y: cy, w: colW, h: ih });
        cy += ih;
      }
      x += colW;
      w -= colW;
    } else {
      // Row along the top edge
      const rowH = sum / w;
      let cx = x;
      for (const item of list) {
        const iw = item.area / rowH;
        rects.push({ key: item.key, x: cx, y, w: iw, h: rowH });
        cx += iw;
      }
      y += rowH;
      h -= rowH;
    }
  };

  for (const item of areas) {
    const side = Math.min(w, h);
    if (row.length === 0 || worst([...row, item], side) <= worst(row, side)) {
      row.push(item);
    } else {
      layoutRow(row);
      row = [item];
    }
  }
  if (row.length > 0) {
    layoutRow(row);
  }
  return rects;
}

/**
 * Treemap grouped by sector, as in T-Invest (decision 24): the map is split into sectors by their sums,
 * each sector into its shares, biggest first. items: { key, weight, group }.
 */
export function groupedSquarify(items, width, height) {
  const groups = new Map();
  for (const item of items) {
    const group = item.group || 'other';
    if (!groups.has(group)) {
      groups.set(group, []);
    }
    groups.get(group).push(item);
  }
  const blocks = [...groups.entries()]
    .map(([key, list]) => ({ key, list: list.sort((a, b) => b.weight - a.weight), weight: list.reduce((s, i) => s + i.weight, 0) }))
    .sort((a, b) => b.weight - a.weight);

  const byKey = new Map(blocks.map((b) => [b.key, b]));
  return squarify(blocks, width, height).flatMap((rect) => squarify(byKey.get(rect.key).list, rect.w, rect.h)
    .map((tile) => ({ ...tile, x: tile.x + rect.x, y: tile.y + rect.y })));
}

// Tile area grows as the square root of the weight, so the leaders do not take half the map (decisions 28, 36).
export const SIZE_POWER = 0.5;

const GAP = 3;

/**
 * Sector-grouped layout where every tile can show at least its ticker (decision 28): a share whose tile
 * is too small for any label is left off the map (it stays in «Смотреть все») and the map is laid out
 * again without it. items: { key, weight, group, ticker }.
 */
export function fitLayout(items, width, height, desktop) {
  let shown = items.map((i) => ({ ...i, weight: i.weight ** SIZE_POWER }));
  const dropped = [];
  for (let pass = 0; pass < 20; pass++) {
    const rects = groupedSquarify(shown, width, height);
    const byKey = new Map(shown.map((i) => [i.key, i]));
    const empty = rects.filter((r) => labelFor(byKey.get(r.key)?.ticker ?? '', '', r.w - GAP, r.h - GAP, desktop) === null);
    if (empty.length === 0 || empty.length === shown.length) {
      return { rects, dropped };
    }
    // The smallest first: removing it makes room for the others.
    const smallest = empty.reduce((a, b) => (a.w * a.h <= b.w * b.h ? a : b));
    dropped.push(smallest.key);
    shown = shown.filter((i) => i.key !== smallest.key);
  }
  return { rects: groupedSquarify(shown, width, height), dropped };
}

// Colour steps (decisions 7, 18, 40): thresholds 0.01 / 0.5 / 1.5 / 3 %; as in T-Invest, a small move is light,
// a big one dark. Grey only below 0.01 %.
export const NEUTRAL = '#5b5d63';
const UP = ['#45be7f', '#2fa463', '#16884a', '#0a6630'];
const DOWN = ['#f47d80', '#e0565b', '#bf2f35', '#9b0d12'];
const STEPS = [0.01, 0.5, 1.5, 3];

export function tileColor(change) {
  if (change === null || change === undefined) {
    return NEUTRAL;
  }
  const abs = Math.abs(change);
  if (abs < STEPS[0]) {
    return NEUTRAL;
  }
  let step = 0;
  while (step < STEPS.length - 1 && abs >= STEPS[step + 1]) {
    step++;
  }
  return change > 0 ? UP[step] : DOWN[step];
}

// Labels, as in T-Invest (decision 37): one size for every tile — 14 px on a phone, 18 px on a desktop — and
// smaller only where it does not fit, 10 px at least; the percent is as big as the ticker. Without room for
// two lines only the ticker is shown. Text is measured exactly, 4 px from the sides and 3 px from top and bottom.
const BASE_FONT = { desktop: 18, phone: 14 };
const MIN_FONT = 10;
const PERCENT_RATIO = 1;
const LINE_HEIGHT = 1.15;
const LINE_GAP = 2;
const PAD_X = 4;
const PAD_Y = 3;
// Without a page (tests in Node) widths are estimated per character.
const TICKER_CHAR = 0.62;
const PERCENT_CHAR = 0.56;

let measureContext;
const widths = new Map();

// Width of the text at 1 px in the page's font: the label scales linearly with the font size.
function unitWidth(text, weight, perChar) {
  const key = `${weight}|${text}`;
  if (!widths.has(key)) {
    if (measureContext === undefined) {
      measureContext = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    }
    let width = text.length * perChar;
    if (measureContext) {
      measureContext.font = `${weight} 100px ${getComputedStyle(document.body).fontFamily}`;
      width = measureContext.measureText(text).width / 100;
    }
    widths.set(key, width);
  }
  return widths.get(key);
}

/**
 * Font sizes { ticker, percent } for a tile (percent null: ticker only), or null when even the ticker at
 * 10 px does not fit (a cut ticker helps nobody).
 */
export function labelFor(ticker, percentText, w, h, desktop) {
  const width = w - 2 * PAD_X;
  const height = h - 2 * PAD_Y;
  const wanted = BASE_FONT[desktop ? 'desktop' : 'phone'];
  const tickerWidth = unitWidth(ticker, 600, TICKER_CHAR);

  if (percentText) {
    const percentWidth = unitWidth(percentText, 400, PERCENT_CHAR);
    let size = Math.floor(Math.min(
      wanted,
      width / Math.max(tickerWidth, percentWidth * PERCENT_RATIO),
      (height - LINE_GAP) / (LINE_HEIGHT * (1 + PERCENT_RATIO)),
    ));
    size = Math.max(size, MIN_FONT);
    const percent = Math.max(Math.floor(size * PERCENT_RATIO), MIN_FONT);
    const fits = tickerWidth * size <= width
      && percentWidth * percent <= width
      && (size + percent) * LINE_HEIGHT + LINE_GAP <= height;
    if (fits) {
      return { ticker: size, percent };
    }
  }

  const size = Math.floor(Math.min(wanted, width / Math.max(tickerWidth, 0.01), height / LINE_HEIGHT));
  return size >= MIN_FONT || (tickerWidth * MIN_FONT <= width && MIN_FONT * LINE_HEIGHT <= height)
    ? { ticker: Math.max(size, MIN_FONT), percent: null }
    : null;
}

// Percent on a tile: no space before % — a shorter line fits more tiles (decisions 36, 37); no sign, the colour
// shows it, except on a grey tile (|change| < 0.01 %) where the colour does not (decision 40).
export function tilePercent(change) {
  const text = formatSignedPercent(change, 2).replace(/\s%$/, '%');
  return tileColor(change) === NEUTRAL ? text : text.replace(/^[+−]/, '');
}
